import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BellIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { byPlannedDate, formatDate, formatPrice } from '../../utils/format';
import { effectiveStatus } from '../../utils/quote';
const ease = [0.23, 1, 0.32, 1] as const;
export function Overview() {
  const {
    cases,
    customerById,
    enquiries,
    quotes,
    dataLoading
  } = useAdminData();
  const newEnquiries = enquiries.filter(item => item.status === 'Ny henvendelse');
  const kpis = [{
    label: 'Nye henvendelser',
    value: newEnquiries.length,
    primary: true
  }, {
    label: 'Aktive sager',
    value: cases.filter(c => !['Færdig', 'Faktureret'].includes(c.status)).length,
    primary: false
  }, {
    label: 'Planlagte opgaver',
    value: cases.filter(c => c.status === 'Planlagt').length,
    primary: false
  }, {
    // Sendte tilbud der endnu ikke er besvaret, plus sager markeret "Tilbud sendt".
    label: 'Afventer tilbud',
    value: quotes.filter(quote => effectiveStatus(quote) === 'Sendt').length + cases.filter(c => c.status === 'Tilbud sendt').length,
    primary: false
  }, {
    label: 'Færdige – ikke faktureret',
    value: cases.filter(c => c.status === 'Færdig').length,
    primary: false
  }];
  const latest = [...cases].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 6);
  const upcoming = cases.filter(c => c.plannedDate && !['Færdig', 'Faktureret'].includes(c.status)).sort(byPlannedDate);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-745" data-mp="b1prru5hnx2s">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-746" data-mp="18jg2pmrw9br9">
        <div data-id="element-747" data-mp="13sg7qn2c53re">
          <p className="text-sm text-ink-muted" data-id="element-748" data-mp="13ry0ywi1r758">God dag</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-749" data-mp="13qd615rtlm6u">
            Overblik
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-750" data-mp="32y3f6c50vdn6">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-751" data-mp="2oba6hpq0d63y" />
          Opret sag
        </Link>
      </div>

      {dataLoading && cases.length === 0 && <p className="mt-6 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-752" data-mp="2t78pexskhvwz">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-753" data-mp="2f8itvmlec4aq" />
          Henter data …
        </p>}

      {newEnquiries.length > 0 && <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease
    }} className="mt-6 rounded-card border border-accent/30 bg-accent-soft p-4 md:p-5" data-id="element-754" data-mp="25cftoma3ufqf">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-755" data-mp="3i6oi28th72r">
            <div className="flex items-start gap-3" data-id="element-756" data-mp="8iuhgtglvxzm">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-accent-deep" data-id="element-757" data-mp="1t0vi1jjdyj5n">
                <BellIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-758" data-mp="1o3mxjcarm318" />
              </span>
              <div data-id="element-759" data-mp="22srhoz14ehbz">
                <ul data-id="element-760" data-mp="22tb00guon7qj">
                  {newEnquiries.slice(0, 3).map(item => <li key={item.id} className="text-[15px] font-medium text-ink" data-id="element-761" data-mp="1xyx3uc874wkt">
                      Ny henvendelse fra {item.name}
                    </li>)}
                </ul>
                {newEnquiries.length > 3 && <p className="mt-1 text-sm text-ink-soft" data-id="element-762" data-mp="2lpsrqy9j77ms">+ {newEnquiries.length - 3} flere</p>}
              </div>
            </div>
            <Link to="/admin/henvendelser" className={buttonClasses('primary', 'sm')} data-id="element-763" data-mp="2griw6mjwa50n">
              Se henvendelser
            </Link>
          </div>
        </motion.div>}

      <ul className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-id="element-764" data-mp="mdzxh9e4ogdr">
        {kpis.map((kpi, index) => <motion.li key={kpi.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        ease,
        delay: index * 0.04
      }} className={`rounded-card border p-5 ${kpi.primary ? 'border-accent/30 bg-accent-soft' : 'border-line bg-white'}`} data-id="element-765" data-mp="39kmdzpk97vdt" data-mpi={index}>
            <p className="font-display text-[2.5rem] font-bold leading-none tracking-tight text-ink" data-id="element-766" data-mp="2qvpg7bpnekqu">
              {kpi.value}
            </p>
            <p className="mt-2 text-[15px] text-ink-soft" data-id="element-767" data-mp="2vu96fob9yip0">{kpi.label}</p>
          </motion.li>)}
      </ul>

      <section className="mt-8 rounded-card border border-line bg-white" data-id="element-768" data-mp="2ztrma5o8o3kz">
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-769" data-mp="34nm4f4o74ahy">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-770" data-mp="2846er9b9veqx">Seneste sager</h2>
          <Link to="/admin/sager" className="text-sm font-medium text-accent hover:text-accent-deep" data-id="element-771" data-mp="2c474hq3tncda">
            Se alle
          </Link>
        </header>

        <div className="hidden md:block" data-id="element-772" data-mp="1zmw8x32ley8j">
          <table className="w-full text-left text-[15px]" data-id="element-773" data-mp="1zo7dv67ryc18">
            <thead data-id="element-774" data-mp="1vl1ac6c1qyyg">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-775" data-mp="1vkhqgk2u60hs">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-776" data-mp="1qly8eurd58up">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-777" data-mp="1lnwltzuqareh">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-778" data-mp="1gx6ist9rz0my">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-779" data-mp="1gutu7x9ihs0x">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-780" data-mp="1byd0mnjjmlgg">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-781" data-mp="17x9p2d8j3bl1" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-782" data-mp="12xey69or6lzz">
              {latest.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-783" data-mp="xxsizupd6voj">
                  <td className="px-5 py-3.5 text-ink" data-id="element-784" data-mp="o9ew2bcsl742">
                    {customerById(item.customerId)?.name ?? item.customerName}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-785" data-mp="t76qbc0wt5bf">
                    <span className="text-ink-faint" data-id="element-786" data-mp="edtnfud7x8lc">#{item.number}</span> {item.title}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-787" data-mp="jbls7oyyop6y">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-788" data-mp="1730vpl2h5cjs"><StatusBadge status={item.status} data-id="element-789" data-mp="1bzrbe7ada64o" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-790" data-mp="1bz966l5q8hkm">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-791" data-mp="128wp52x4f6j8">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-792" data-mp="1q025rkavy2ul">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-793" data-mp="3ff350086qohp">
          {latest.map(item => <li key={item.id} data-id="element-794" data-mp="3ahkq7t7hlf1s">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-795" data-mp="3p6qye6oayflt">
                <div className="flex items-start justify-between gap-3" data-id="element-796" data-mp="3p8tlahu5rsmb">
                  <div className="min-w-0" data-id="element-797" data-mp="3k85yy9gvjzyf">
                    <p className="truncate text-[15px] font-medium text-ink" data-id="element-798" data-mp="2wi1xa7n8yaa2">{item.title}</p>
                    <p className="mt-0.5 text-sm text-ink-muted" data-id="element-799" data-mp="2riyp4ebyznxv">
                      #{item.number} · {customerById(item.customerId)?.name ?? item.customerName}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-800" data-mp="31hehekmqh38y" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-801" data-mp="31j90ruicwnax">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && latest.length === 0 && <p className="px-5 py-10 text-center text-sm text-ink-muted" data-id="element-802" data-mp="2a7joxeoy7dv7">Der er ingen sager endnu.</p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white" data-id="element-803" data-mp="2k7u4h5yggxdj">
        <header className="border-b border-line px-5 py-4" data-id="element-804" data-mp="2k5r2ronxxfsz">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-805" data-mp="2ozlu9e9sez2m">Kommende opgaver</h2>
        </header>
        <ul className="divide-y divide-line" data-id="element-806" data-mp="2szm7wwp2b68m">
          {upcoming.map(item => <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-id="element-807" data-mp="2xxxjbbv09osy">
              <div className="min-w-0" data-id="element-808" data-mp="32zmuqsm544hk">
                <p className="truncate text-[15px] font-medium text-ink" data-id="element-809" data-mp="32wh8rm8t5iq2">{item.title}</p>
                <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-810" data-mp="37vbx13pjnzhk">
                  {customerById(item.customerId)?.name ?? item.customerName} · {item.address}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-811" data-mp="1dl8bgntw59tg">
                <span className="whitespace-nowrap text-sm text-ink-soft" data-id="element-812" data-mp="3qugixpgfb2b8">{formatDate(item.plannedDate)}</span>
                <StatusBadge status={item.status} data-id="element-813" data-mp="3m1dnfbs35ise" />
                <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-814" data-mp="3i13gnwwgyoch">
                  Åbn
                </Link>
              </div>
            </li>)}
          {upcoming.length === 0 && <li className="px-5 py-8 text-center text-sm text-ink-muted" data-id="element-815" data-mp="dm3p8erbgxpw">Ingen planlagte opgaver endnu.</li>}
        </ul>
      </section>
    </div>;
}